import { isObject } from 'lodash'

import type { useTranslation } from '@/app/translations/translations-hooks'
import { USER_ROLE } from '@/hooks/access'
import type { UserRole } from '@/types'

import type { LeasedAsset } from '../resources'

export function monthDiff(startDate: Date, endDate: Date) {
  let months = 0
  months = (endDate.getFullYear() - startDate.getFullYear()) * 12
  months -= startDate.getMonth()
  months += endDate.getMonth()
  return months
}

export function getFilterByKey({
  queryAssetAge,
  queryAssetType,
  search,
}: {
  queryAssetAge: string | null
  queryAssetType: string | null
  search: Location['search']
}) {
  const typeIdx = search.indexOf('assetType')
  const ageIdx = search.indexOf('assetAge')
  let filterByKey = ''

  if ((queryAssetType && !queryAssetAge) || (queryAssetType && typeIdx < ageIdx)) {
    filterByKey = 'assetType'
    return filterByKey
  }
  if ((queryAssetAge && !queryAssetType) || (queryAssetAge && ageIdx < typeIdx)) {
    filterByKey = 'assetAge'
    return filterByKey
  }

  return ''
}

export function getSelectOptions<T extends string>(
  options:
    | { id: number | string; label: T | { id: string; values: Record<string, string> } }[]
    | undefined,
  translate: ReturnType<typeof useTranslation>
) {
  return (options || []).map(option => ({
    label: isObject(option.label)
      ? translate(option.label.id, option.label.values)
      : translate(option.label),
    value: option.id,
  }))
}

export function getIsWithinTimePeriod(daysAhead: number, targetDate: Date) {
  const now: Date = new Date()
  const futureDate: Date = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000)
  return targetDate <= futureDate
}

const MonthExtensionPattern = /^(\d+)_MONTH_EXTENSION$/

const getMonthNumber = (option: string): number | null => {
  const match = option.match(MonthExtensionPattern)
  return match ? parseInt(match[1], 10) : null
}

const sortFn = (a: string | number, b: string | number) => {
  if (typeof a === 'number' && typeof b === 'string') return 1
  if (typeof a === 'string' && typeof b === 'number') return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return 0
}

export const filterAndSortOptions = (activeEndingOptions: string[]) => {
  const filtered = activeEndingOptions.filter(option => option !== 'BUYOUT') || []
  const optionsByNumber = filtered
    .map(option => {
      const number = getMonthNumber(option)
      return number === null ? option : number
    })
    .sort(sortFn)
  return optionsByNumber
}

export const isLeasedAssets = (obj: any): obj is LeasedAsset =>
  (typeof obj === 'object' &&
    obj !== null &&
    'leaseCurrentEndingOption' in obj &&
    typeof obj.leaseCurrentEndingOption === 'string') ||
  obj.leaseCurrentEndingOption === null

export const isLeasedAssetsList = (arr: any[]): arr is LeasedAsset[] =>
  Array.isArray(arr) && arr.every(isLeasedAssets)

const LEASED_ADMIN_ROLES = [
  USER_ROLE.ADMIN,
  USER_ROLE.CUSTOMER_HEAD_ADMIN,
  USER_ROLE.CUSTOMER_ADMIN,
  USER_ROLE.IT_USER,
]

export const useLeasedFeatureAccess = (role: Partial<UserRole>) => LEASED_ADMIN_ROLES.includes(role)
